import React, { useState, useEffect, useCallback } from "react";
import {
  makeStyles,
  Typography,
  TextField,
  Button,
  IconButton,
  Paper,
  Tooltip,
} from "@material-ui/core";
import {
  Add as AddIcon,
  Edit as EditIcon,
  CheckCircle,
  DeleteOutline as DeleteOutlineIcon,
  AttachFile,
  Cancel as CancelIcon,
  SubdirectoryArrowRight,
} from "@material-ui/icons";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { head } from "lodash";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    marginTop: theme.spacing(1),
  },
  headerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1),
  },
  nodeWrapper: {
    marginBottom: theme.spacing(0.75),
  },
  optionCard: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(0.75, 1.5),
    borderRadius: 6,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    gap: theme.spacing(1),
    transition: "box-shadow 0.15s",
    "&:hover": {
      boxShadow: theme.shadows[1],
    },
  },
  optionBadge: {
    minWidth: 24,
    height: 24,
    borderRadius: "50%",
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.68rem",
    fontWeight: 700,
    flexShrink: 0,
  },
  optionContent: {
    flex: 1,
    minWidth: 0,
  },
  optionTitle: {
    fontWeight: 600,
    fontSize: "0.875rem",
    color: theme.palette.text.primary,
    lineHeight: 1.3,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  optionMessage: {
    fontSize: "0.72rem",
    color: theme.palette.text.secondary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  optionActions: {
    display: "flex",
    alignItems: "center",
    flexShrink: 0,
    gap: 0,
  },
  editBox: {
    padding: theme.spacing(1.5),
    border: `1px solid ${theme.palette.primary.light}`,
    borderRadius: 6,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.03)"
        : "rgba(63,81,181,0.03)",
  },
  editField: {
    marginBottom: theme.spacing(1.25),
  },
  editActions: {
    display: "flex",
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.5),
    flexWrap: "wrap",
  },
  childrenContainer: {
    marginLeft: theme.spacing(2.5),
    paddingLeft: theme.spacing(1.5),
    borderLeft: `2px solid ${theme.palette.divider}`,
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
  },
  attachmentPreview: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(0.75),
    backgroundColor: theme.palette.type === "dark" ? "#2f3646" : "#f5f5f5",
    borderRadius: 4,
    border: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(1),
  },
  emptyText: {
    fontSize: "0.82rem",
    color: theme.palette.text.secondary,
    padding: theme.spacing(1, 0),
  },
}));

const createNewOption = (queueId, parentId = null, optionNumber = 1) => ({
  title: "",
  message: "",
  edition: true,
  option: optionNumber,
  queueId,
  parentId,
  children: [],
  mediaPath: null,
  mediaName: null,
});

// Inner MediaUpload sub-component
function MediaUpload({ initialPath, initialName, onUpload, onDelete, classes }) {
  const [file, setFile] = useState(null);
  const fileInputRef = React.useRef(null);

  const handleFileChange = (e) => {
    const uploaded = head(e.target.files);
    if (uploaded) {
      setFile(uploaded);
      onUpload(uploaded);
    }
  };

  const removeFile = () => {
    if (onDelete) onDelete();
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const fileName = file ? file.name : initialName;

  if (!fileName) {
    return (
      <>
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
        <Button
          size="small"
          startIcon={<AttachFile />}
          onClick={() => fileInputRef.current.click()}
          style={{ marginBottom: 8 }}
        >
          Anexar Arquivo
        </Button>
      </>
    );
  }

  return (
    <div className={classes.attachmentPreview}>
      <Typography variant="body2" noWrap style={{ flex: 1 }}>
        {fileName}
      </Typography>
      <IconButton size="small" color="secondary" onClick={removeFile}>
        <DeleteOutlineIcon fontSize="small" />
      </IconButton>
    </div>
  );
}

// Recursive tree component
export function QueueOptionStepper({ queueId, options, onUpdate, reload }) {
  const classes = useStyles();
  const [localOptions, setLocalOptions] = useState(options);

  useEffect(() => {
    setLocalOptions(JSON.parse(JSON.stringify(options)));
  }, [options]);

  const handleChange = (index, field, value) => {
    const updated = [...localOptions];
    updated[index] = { ...updated[index], [field]: value };
    setLocalOptions(updated);
  };

  const toggleEdition = (index) => {
    const updated = [...localOptions];
    updated[index] = { ...updated[index], edition: !updated[index].edition };
    setLocalOptions(updated);
  };

  const handleSave = async (index) => {
    const option = localOptions[index];
    if (!option.title || !option.title.trim()) {
      alert("O título da opção é obrigatório.");
      return;
    }
    try {
      if (option.id) {
        const { data } = await api.put(`/queue-options/${option.id}`, option);
        // Edição não altera a numeração — atualiza apenas o item localmente.
        const updated = [...localOptions];
        updated[index] = { ...data, edition: false, children: option.children };
        setLocalOptions(updated);
        if (onUpdate) onUpdate([...updated]);
      } else {
        await api.post("/queue-options", option);
        // Criação renumera os irmãos no backend — recarrega a árvore para
        // refletir a numeração canônica (1, 2, 3...) em todos os níveis.
        if (reload) await reload();
      }
    } catch (err) {
      toastError(err);
    }
  };

  const handleDelete = async (index) => {
    const option = localOptions[index];
    if (option.id) {
      try {
        await api.delete(`/queue-options/${option.id}`);
      } catch (err) {
        toastError(err);
        return;
      }
      // Exclusão renumera os irmãos no backend — recarrega a árvore para
      // manter a numeração consistente com o que foi persistido.
      if (reload) {
        await reload();
        return;
      }
    }
    const filtered = localOptions.filter((_, i) => i !== index);
    const reordered = filtered.map((opt, i) => ({ ...opt, option: i + 1 }));
    setLocalOptions(reordered);
    if (onUpdate) onUpdate(reordered);
  };

  const handleAddChild = (index) => {
    const parent = localOptions[index];
    const newOption = createNewOption(queueId, parent.id, parent.children.length + 1);
    const updated = [...localOptions];
    updated[index] = {
      ...parent,
      children: [...parent.children, newOption],
    };
    setLocalOptions(updated);
    if (onUpdate) onUpdate(updated);
  };

  const handleUpload = async (file, optionId) => {
    if (!file || !optionId) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      await api.post(`/queue-options/${optionId}/media-upload`, formData);
    } catch (err) {
      toastError(err);
    }
  };

  const renderChildren = (option, index) => {
    if (!option.children || option.children.length === 0) return null;
    return (
      <div className={classes.childrenContainer}>
        <QueueOptionStepper
          queueId={queueId}
          options={option.children}
          reload={reload}
          onUpdate={(children) => {
            const updated = JSON.parse(JSON.stringify(localOptions));
            updated[index].children = children;
            setLocalOptions(updated);
            if (onUpdate) onUpdate(updated);
          }}
        />
      </div>
    );
  };

  return (
    <div>
      {localOptions.map((option, index) => (
        <div
          key={option.id ? `opt-${option.id}` : `new-${index}-${option.option}`}
          className={classes.nodeWrapper}
        >
          {/* ── Edit form ── */}
          {option.edition ? (
            <div className={classes.editBox}>
              <TextField
                label="Título da Opção"
                value={option.title}
                onChange={(e) => handleChange(index, "title", e.target.value)}
                size="small"
                variant="outlined"
                fullWidth
                autoFocus
                className={classes.editField}
              />
              <TextField
                label="Mensagem ao selecionar"
                multiline
                rows={3}
                value={option.message}
                onChange={(e) => handleChange(index, "message", e.target.value)}
                size="small"
                variant="outlined"
                fullWidth
                className={classes.editField}
              />
              <MediaUpload
                classes={classes}
                initialPath={option.mediaPath}
                initialName={option.mediaName}
                onUpload={(file) => handleUpload(file, option.id)}
                onDelete={async () => {
                  if (option.id) {
                    await api.delete(`/queue-options/${option.id}/media-upload`);
                  }
                }}
              />
              <div className={classes.editActions}>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  startIcon={<CheckCircle />}
                  onClick={() => handleSave(index)}
                >
                  Salvar
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={() => {
                    if (!option.id) handleDelete(index);
                    else toggleEdition(index);
                  }}
                >
                  Cancelar
                </Button>
                {option.id && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="secondary"
                    startIcon={<DeleteOutlineIcon />}
                    onClick={() => handleDelete(index)}
                  >
                    Excluir
                  </Button>
                )}
              </div>
            </div>
          ) : (
            /* ── Card view ── */
            <div className={classes.optionCard}>
              <div className={classes.optionBadge}>{option.option}</div>
              <div className={classes.optionContent}>
                <Typography className={classes.optionTitle}>
                  {option.title || (
                    <em style={{ fontWeight: 400, opacity: 0.6 }}>(Sem título)</em>
                  )}
                </Typography>
                {option.message ? (
                  <Typography className={classes.optionMessage}>
                    {option.message}
                  </Typography>
                ) : null}
              </div>
              <div className={classes.optionActions}>
                {option.id && (
                  <Tooltip title="Adicionar subopção">
                    <IconButton
                      size="small"
                      onClick={() => handleAddChild(index)}
                    >
                      <SubdirectoryArrowRight fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Editar">
                  <IconButton size="small" onClick={() => toggleEdition(index)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Excluir">
                  <IconButton
                    size="small"
                    color="secondary"
                    onClick={() => handleDelete(index)}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </div>
            </div>
          )}

          {/* Children — always visible, indented */}
          {renderChildren(option, index)}
        </div>
      ))}
    </div>
  );
}

// Builds a sorted tree from a flat list of options using parentId
const buildTree = (flat) => {
  const map = {};
  flat.forEach((opt) => {
    map[opt.id] = { ...opt, children: [], edition: false };
  });
  const roots = [];
  flat.forEach((opt) => {
    if (!opt.parentId) {
      roots.push(map[opt.id]);
    } else if (map[opt.parentId]) {
      map[opt.parentId].children.push(map[opt.id]);
    }
  });
  const sort = (nodes) => {
    nodes.sort((a, b) => a.option - b.option);
    nodes.forEach((n) => sort(n.children));
    return nodes;
  };
  return sort(roots);
};

// Root component
export function QueueOptions({ queueId }) {
  const classes = useStyles();
  const [options, setOptions] = useState([]);

  const fetchOptions = useCallback(async () => {
    if (!queueId) return;
    try {
      // Fetch all levels at once (no parentId filter → backend returns everything)
      const { data } = await api.get("/queue-options", {
        params: { queueId },
      });
      setOptions(buildTree(data));
    } catch (err) {
      toastError(err);
    }
  }, [queueId]);

  useEffect(() => {
    fetchOptions();
  }, [fetchOptions]);

  const addRootOption = () => {
    const newOpt = createNewOption(queueId, null, options.length + 1);
    setOptions((prev) => [...prev, newOpt]);
  };

  const handleUpdate = (updatedOptions) => {
    setOptions(JSON.parse(JSON.stringify(updatedOptions)));
  };

  if (!queueId) return null;

  return (
    <div className={classes.root}>
      <div className={classes.headerRow}>
        <Typography variant="subtitle2" color="textSecondary">
          Menu de Opções do Bot
        </Typography>
        <Button
          startIcon={<AddIcon />}
          size="small"
          color="primary"
          variant="outlined"
          onClick={addRootOption}
        >
          Nova Opção
        </Button>
      </div>

      {options.length === 0 ? (
        <Typography className={classes.emptyText}>
          Nenhuma opção configurada. Clique em "Nova Opção" para começar.
        </Typography>
      ) : (
        <QueueOptionStepper
          queueId={queueId}
          options={options}
          onUpdate={handleUpdate}
          reload={fetchOptions}
        />
      )}
    </div>
  );
}
